import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { agentMonitorAPI, liveCallsAPI } from '../../services/api';
import {
  Users, Phone, PhonePause, PhoneOff, UserCheck, UserX,
  RefreshCw, Clock, Activity, ChevronDown, Search,
  Headphones, Coffee, Eye, MessageSquare, UserPlus, ArrowRightLeft,
  PhoneIncoming, PhoneOutgoing, Mic, MicOff, Volume2, Wifi, WifiOff
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Derive WebSocket URL from API URL
const getWebSocketUrl = () => {
  const apiUrl = API || '';
  const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
  const baseUrl = apiUrl.replace(/^https?:\/\//, '');
  return `${wsProtocol}://${baseUrl}`;
};

export default function AgentMonitorPage() {
  const navigate = useNavigate();
  const { clientUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    total_agents: 0,
    logged_in: 0,
    available: 0,
    on_call: 0,
    paused: 0,
    logged_out: 0
  });
  const [agents, setAgents] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [liveCalls, setLiveCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [activeTab, setActiveTab] = useState('agents'); // 'agents' or 'live-calls'
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const clientId = clientUser?.id;

  const fetchAgentStatus = useCallback(async () => {
    if (!clientId) return;
    
    try {
      const response = await agentMonitorAPI.getSummary(clientId);
      
      if (response.data.success) {
        setSummary(response.data.summary);
        setAgents(response.data.agents);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Error fetching agent status:', error);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const fetchLiveCalls = useCallback(async () => {
    if (!clientId) return;
    
    try {
      const response = await liveCallsAPI.getCalls(clientId);
      
      if (response.data.success) {
        setLiveCalls(response.data.calls);
      }
    } catch (error) {
      console.error('Error fetching live calls:', error);
    }
  }, [clientId]);

  // Supervisor actions
  const handleMonitor = async (callId) => {
    setActionLoading(callId);
    try {
      await liveCallsAPI.monitor(clientId, clientUser?.id, callId);
      fetchLiveCalls();
    } catch (error) {
      console.error('Error starting monitor:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleWhisper = async (callId, message) => {
    setActionLoading(callId);
    try {
      await liveCallsAPI.whisper(clientId, clientUser?.id, callId, message);
      fetchLiveCalls();
    } catch (error) {
      console.error('Error whispering:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBarge = async (callId) => {
    setActionLoading(callId);
    try {
      await liveCallsAPI.barge(clientId, clientUser?.id, callId);
      fetchLiveCalls();
    } catch (error) {
      console.error('Error barging:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleTakeover = async (callId) => {
    if (!window.confirm('Are you sure you want to take over this call? The current agent will be removed.')) {
      return;
    }
    setActionLoading(callId);
    try {
      await liveCallsAPI.takeover(clientId, clientUser?.id, callId);
      fetchLiveCalls();
    } catch (error) {
      console.error('Error taking over:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleStopMonitor = async (callId) => {
    setActionLoading(callId);
    try {
      await liveCallsAPI.stopMonitoring(clientId, clientUser?.id, callId);
      fetchLiveCalls();
    } catch (error) {
      console.error('Error stopping monitor:', error);
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    // Initial fetch via HTTP
    fetchAgentStatus();
    fetchLiveCalls();
    
    // Setup WebSocket connection
    const connectWebSocket = () => {
      if (!clientId) return;
      
      const wsUrl = `${getWebSocketUrl()}/api/ws/agent-monitor?client_id=${clientId}`;
      console.log('Connecting to WebSocket:', wsUrl);
      
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        
        ws.onopen = () => {
          console.log('WebSocket connected');
          setWsConnected(true);
        };
        
        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            console.log('WebSocket message:', message.type);
            
            switch (message.type) {
              case 'initial_state':
              case 'refresh':
                // Full state update
                if (message.data) {
                  setSummary(message.data.summary);
                  setAgents(message.data.agents);
                  setLiveCalls(message.data.live_calls || []);
                  setLastUpdated(new Date());
                  setLoading(false);
                }
                break;
                
              case 'agent_status_change':
                // Incremental agent update
                if (message.agent) {
                  setAgents(prevAgents => {
                    const updatedAgents = prevAgents.map(agent => 
                      agent.user_id === message.agent.user_id 
                        ? { ...agent, ...message.agent }
                        : agent
                    );
                    return updatedAgents;
                  });
                  // Update summary based on status changes
                  fetchAgentStatus(); // Quick refresh for accurate summary
                  setLastUpdated(new Date());
                }
                break;
                
              case 'call_started':
              case 'call_answered':
              case 'call_update':
                // Live call update
                if (message.call) {
                  setLiveCalls(prevCalls => {
                    const existingIndex = prevCalls.findIndex(c => c.call_id === message.call.call_id || c.id === message.call.id);
                    if (existingIndex >= 0) {
                      const updated = [...prevCalls];
                      updated[existingIndex] = { ...updated[existingIndex], ...message.call };
                      return updated;
                    }
                    return [...prevCalls, message.call];
                  });
                  setLastUpdated(new Date());
                }
                break;
                
              case 'call_ended':
                // Remove ended call
                if (message.call) {
                  setLiveCalls(prevCalls => 
                    prevCalls.filter(c => c.call_id !== message.call.call_id && c.id !== message.call.id)
                  );
                  setLastUpdated(new Date());
                }
                break;
                
              case 'heartbeat':
                // Send pong
                if (ws.readyState === WebSocket.OPEN) {
                  ws.send(JSON.stringify({ type: 'ping' }));
                }
                break;
                
              default:
                break;
            }
          } catch (e) {
            console.error('Error parsing WebSocket message:', e);
          }
        };
        
        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          setWsConnected(false);
        };
        
        ws.onclose = () => {
          console.log('WebSocket disconnected');
          setWsConnected(false);
          wsRef.current = null;
          
          // Reconnect after 3 seconds if autoRefresh is enabled
          if (autoRefresh) {
            reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
          }
        };
        
      } catch (error) {
        console.error('Failed to create WebSocket:', error);
        setWsConnected(false);
      }
    };
    
    // Connect WebSocket if autoRefresh is enabled
    if (autoRefresh) {
      connectWebSocket();
    } else {
      // Fallback to HTTP polling every 10 seconds when WebSocket is not used
      const interval = setInterval(() => {
        fetchAgentStatus();
        fetchLiveCalls();
      }, 10000);
      
      return () => {
        clearInterval(interval);
        if (wsRef.current) {
          wsRef.current.close();
        }
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };
    }
    
    // Cleanup
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [clientId, autoRefresh]);
  
  // Manual refresh function
  const handleManualRefresh = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'refresh' }));
    } else {
      fetchAgentStatus();
      fetchLiveCalls();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-green-500';
      case 'on_call':
        return 'bg-blue-500';
      case 'paused':
        return 'bg-yellow-500';
      case 'logged_out':
        return 'bg-gray-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-green-500/10 border-green-500/30';
      case 'on_call':
        return 'bg-blue-500/10 border-blue-500/30';
      case 'paused':
        return 'bg-yellow-500/10 border-yellow-500/30';
      case 'logged_out':
        return 'bg-gray-500/10 border-gray-500/30';
      default:
        return 'bg-gray-500/10 border-gray-500/30';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return <UserCheck className="w-4 h-4" />;
      case 'on_call':
        return <Headphones className="w-4 h-4" />;
      case 'paused':
        return <Coffee className="w-4 h-4" />;
      case 'logged_out':
        return <UserX className="w-4 h-4" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const mins = Math.floor(seconds / 60);
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) {
      return `${hrs}h ${mins % 60}m`;
    }
    return `${mins}m`;
  };

  const formatCallDuration = (seconds) => {
    if (!seconds || seconds < 0) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const filteredAgents = agents.filter(agent => {
    const matchesFilter = filter === 'all' || agent.status === filter;
    const matchesSearch = agent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agent.username?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const statCards = [
    { 
      label: 'Total Agents', 
      value: summary.total_agents, 
      icon: Users, 
      color: 'text-white',
      bgColor: 'bg-gray-700'
    },
    { 
      label: 'Logged In', 
      value: summary.logged_in, 
      icon: UserCheck, 
      color: 'text-teal-400',
      bgColor: 'bg-teal-500/10'
    },
    { 
      label: 'Available', 
      value: summary.available, 
      icon: UserCheck, 
      color: 'text-green-400',
      bgColor: 'bg-green-500/10'
    },
    { 
      label: 'On Call', 
      value: summary.on_call, 
      icon: Headphones, 
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10'
    },
    { 
      label: 'Paused', 
      value: summary.paused, 
      icon: Coffee, 
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/10'
    },
    { 
      label: 'Logged Out', 
      value: summary.logged_out, 
      icon: UserX, 
      color: 'text-gray-400',
      bgColor: 'bg-gray-500/10'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white p-6" data-testid="agent-monitor-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Agent Monitor</h1>
          <p className="text-gray-400 text-sm">Real-time agent status and live call supervision</p>
        </div>
        <div className="flex items-center gap-4">
          {/* WebSocket Status Indicator */}
          <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${
            wsConnected 
              ? 'bg-green-500/20 text-green-400' 
              : 'bg-gray-700 text-gray-400'
          }`}>
            {wsConnected ? (
              <>
                <Wifi className="w-3 h-3" />
                <span>Live</span>
              </>
            ) : (
              <>
                <WifiOff className="w-3 h-3" />
                <span>Polling</span>
              </>
            )}
          </div>
          {lastUpdated && (
            <span className="text-gray-500 text-sm">
              Updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-400">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-600 text-teal-400 focus:ring-teal-400"
            />
            Auto-refresh
          </label>
          <button
            onClick={handleManualRefresh}
            className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {statCards.map((stat) => (
          <div
            key={stat.label}
            className={`${stat.bgColor} border border-gray-800 rounded-xl p-4`}
            data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
          >
            <div className="flex items-center justify-between mb-2">
              <stat.icon className={`w-5 h-5 ${stat.color}`} />
              <span className={`text-2xl font-bold ${stat.color}`}>{stat.value}</span>
            </div>
            <p className="text-gray-400 text-sm">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('agents')}
          className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'agents'
              ? 'bg-teal-400/20 text-teal-400 border border-teal-400/30'
              : 'bg-gray-800 text-gray-400 hover:text-white border border-gray-700'
          }`}
          data-testid="tab-agents"
        >
          <Users className="w-4 h-4" />
          Agents ({agents.length})
        </button>
        <button
          onClick={() => setActiveTab('live-calls')}
          className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'live-calls'
              ? 'bg-purple-400/20 text-purple-400 border border-purple-400/30'
              : 'bg-gray-800 text-gray-400 hover:text-white border border-gray-700'
          }`}
          data-testid="tab-live-calls"
        >
          <Phone className="w-4 h-4" />
          Live Calls ({liveCalls.length})
          {liveCalls.length > 0 && (
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
          )}
        </button>
      </div>

      {/* AGENTS TAB */}
      {activeTab === 'agents' && (
        <>
          {/* Filters and Search */}
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search agents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-500 outline-none focus:border-teal-400"
                  data-testid="search-input"
                />
              </div>
              
              {/* Filter */}
              <div className="flex gap-2">
                {['all', 'available', 'on_call', 'paused', 'logged_out'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilter(status)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filter === status
                        ? 'bg-teal-400/20 text-teal-400 border border-teal-400/30'
                        : 'bg-gray-800 text-gray-400 hover:text-white border border-gray-700'
                    }`}
                    data-testid={`filter-${status}`}
                  >
                    {status === 'all' ? 'All' : 
                     status === 'on_call' ? 'On Call' :
                     status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Agent List */}
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-800">
              <h2 className="text-lg font-semibold">
                Agents ({filteredAgents.length})
              </h2>
            </div>
            
            {filteredAgents.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No agents found</p>
                {filter !== 'all' && (
                  <button
                    onClick={() => setFilter('all')}
                    className="text-teal-400 text-sm mt-2 hover:underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {filteredAgents.map((agent) => (
                  <div
                    key={agent.user_id}
                    className={`p-4 hover:bg-gray-800/50 transition-colors ${getStatusBgColor(agent.status)}`}
                    data-testid={`agent-row-${agent.user_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Status Indicator */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStatusColor(agent.status)}`}>
                          {getStatusIcon(agent.status)}
                        </div>
                        
                        {/* Agent Info */}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{agent.name}</span>
                            <span className="text-gray-500 text-sm">@{agent.username}</span>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-gray-400 mt-1">
                            <span className={`capitalize ${
                              agent.status === 'available' ? 'text-green-400' :
                              agent.status === 'on_call' ? 'text-blue-400' :
                              agent.status === 'paused' ? 'text-yellow-400' :
                              'text-gray-500'
                            }`}>
                              {agent.status === 'on_call' ? 'On Call' : 
                               agent.status === 'logged_out' ? 'Logged Out' :
                               agent.status.charAt(0).toUpperCase() + agent.status.slice(1)}
                            </span>
                            {agent.pause_reason && (
                              <span className="text-yellow-500">({agent.pause_reason})</span>
                            )}
                            {agent.current_call_number && (
                              <span className="text-blue-400 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {agent.current_call_number}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Stats */}
                      <div className="flex items-center gap-6 text-sm">
                        <div className="text-center">
                          <p className="text-gray-400">Calls Today</p>
                          <p className="text-white font-medium">{agent.calls_handled_today || 0}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-gray-400">Talk Time</p>
                          <p className="text-white font-medium">{formatDuration(agent.total_talk_time_today)}</p>
                        </div>
                        {agent.logged_in_at && (
                          <div className="text-center">
                            <p className="text-gray-400">Logged In</p>
                            <p className="text-white font-medium">{formatTime(agent.logged_in_at)}</p>
                          </div>
                        )}
                        {agent.last_activity && (
                          <div className="text-center">
                            <p className="text-gray-400">Last Activity</p>
                            <p className="text-white font-medium">{formatTime(agent.last_activity)}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* LIVE CALLS TAB */}
      {activeTab === 'live-calls' && (
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Phone className="w-5 h-5 text-purple-400" />
              Live Calls ({liveCalls.length})
            </h2>
            {liveCalls.length > 0 && (
              <span className="text-xs text-purple-400 bg-purple-400/10 px-2 py-1 rounded">
                {liveCalls.length} active
              </span>
            )}
          </div>
          
          {liveCalls.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Phone className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg mb-2">No Active Calls</p>
              <p className="text-sm">Live calls will appear here when agents are on calls</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-800">
              {liveCalls.map((call) => (
                <div
                  key={call.call_id}
                  className="p-4 hover:bg-gray-800/30 transition-colors"
                  data-testid={`live-call-${call.call_id}`}
                >
                  <div className="flex items-start justify-between">
                    {/* Call Info */}
                    <div className="flex items-start gap-4">
                      {/* Direction Icon */}
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        call.direction === 'inbound' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {call.direction === 'inbound' ? (
                          <PhoneIncoming className="w-5 h-5" />
                        ) : (
                          <PhoneOutgoing className="w-5 h-5" />
                        )}
                      </div>
                      
                      {/* Call Details */}
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-medium text-white">
                            {call.direction === 'inbound' ? call.from_number : call.to_number}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            call.status === 'answered' || call.status === 'active' || call.status === 'in_progress'
                              ? 'bg-green-500/20 text-green-400'
                              : call.status === 'ringing'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {call.status}
                          </span>
                          {call.is_monitored && (
                            <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              Monitored
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatCallDuration(call.duration)}
                          </span>
                          {call.agent_name && (
                            <span className="flex items-center gap-1">
                              <Headphones className="w-3 h-3" />
                              {call.agent_name}
                            </span>
                          )}
                          {call.ai_agent_name && (
                            <span className="text-teal-400">
                              AI: {call.ai_agent_name}
                            </span>
                          )}
                          {call.campaign_name && (
                            <span className="text-orange-400">
                              Campaign: {call.campaign_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Supervisor Actions */}
                    <div className="flex items-center gap-2">
                      {/* Monitor / Stop Monitor */}
                      {call.is_monitored ? (
                        <button
                          onClick={() => handleStopMonitor(call.call_id)}
                          disabled={actionLoading === call.call_id}
                          className="p-2 bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 rounded-lg transition-colors flex items-center gap-1 text-sm"
                          title="Stop Monitoring"
                          data-testid={`stop-monitor-${call.call_id}`}
                        >
                          <Eye className="w-4 h-4" />
                          <span className="hidden md:inline">Stop</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleMonitor(call.call_id)}
                          disabled={actionLoading === call.call_id}
                          className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex items-center gap-1 text-sm"
                          title="Listen (Silent Monitor)"
                          data-testid={`monitor-${call.call_id}`}
                        >
                          <Eye className="w-4 h-4" />
                          <span className="hidden md:inline">Listen</span>
                        </button>
                      )}
                      
                      {/* Whisper */}
                      <button
                        onClick={() => {
                          const message = window.prompt('Enter whisper message (only agent will hear):');
                          if (message) handleWhisper(call.call_id, message);
                        }}
                        disabled={actionLoading === call.call_id}
                        className="p-2 bg-gray-700 hover:bg-yellow-500/20 hover:text-yellow-400 rounded-lg transition-colors flex items-center gap-1 text-sm"
                        title="Whisper to Agent"
                        data-testid={`whisper-${call.call_id}`}
                      >
                        <MessageSquare className="w-4 h-4" />
                        <span className="hidden md:inline">Whisper</span>
                      </button>
                      
                      {/* Barge */}
                      <button
                        onClick={() => handleBarge(call.call_id)}
                        disabled={actionLoading === call.call_id}
                        className="p-2 bg-gray-700 hover:bg-blue-500/20 hover:text-blue-400 rounded-lg transition-colors flex items-center gap-1 text-sm"
                        title="Join Call (Both Parties Hear)"
                        data-testid={`barge-${call.call_id}`}
                      >
                        <UserPlus className="w-4 h-4" />
                        <span className="hidden md:inline">Barge</span>
                      </button>
                      
                      {/* Takeover */}
                      <button
                        onClick={() => handleTakeover(call.call_id)}
                        disabled={actionLoading === call.call_id}
                        className="p-2 bg-gray-700 hover:bg-red-500/20 hover:text-red-400 rounded-lg transition-colors flex items-center gap-1 text-sm"
                        title="Take Over Call"
                        data-testid={`takeover-${call.call_id}`}
                      >
                        <ArrowRightLeft className="w-4 h-4" />
                        <span className="hidden md:inline">Takeover</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Supervisor Actions Legend */}
          <div className="px-4 py-3 border-t border-gray-800 bg-gray-900/50">
            <div className="flex flex-wrap gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3" /> Listen: Silent monitoring
              </span>
              <span className="flex items-center gap-1">
                <MessageSquare className="w-3 h-3" /> Whisper: Talk to agent only
              </span>
              <span className="flex items-center gap-1">
                <UserPlus className="w-3 h-3" /> Barge: Join as 3rd party
              </span>
              <span className="flex items-center gap-1">
                <ArrowRightLeft className="w-3 h-3" /> Takeover: Replace agent
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Live Activity Indicator */}
      {autoRefresh && (
        <div className={`fixed bottom-6 right-6 flex items-center gap-2 border rounded-full px-4 py-2 ${
          wsConnected 
            ? 'bg-green-900/50 border-green-700' 
            : 'bg-gray-900 border-gray-700'
        }`}>
          <div className={`w-2 h-2 rounded-full animate-pulse ${
            wsConnected ? 'bg-green-500' : 'bg-yellow-500'
          }`}></div>
          <span className="text-gray-400 text-sm">
            {wsConnected ? 'Live (WebSocket)' : 'Polling'}
          </span>
        </div>
      )}
    </div>
  );
}
